"use client";

import Button from "@/components/design-system/button/button";
import Dropdown from "@/components/design-system/dropdown";
import TextInputField, { type TextInputFieldProps } from "@/components/design-system/textInputField/textInputField";
import { useGetUsStatesQuery } from "@/store/userApi";
import PlusSolidIcon from "public/next-public/common/icons/plus-solid.svg";
import { useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { NumberFormatBase, NumberFormatValues, PatternFormat } from "react-number-format";
import styles from "./addressForm.module.scss";

export enum FieldMaxLengths {
  NAME_LENGTH = 50,
  ADDRESS_LENGTH = 35,
}

export default function AddressForm({
  as: Component = Container,
  onChangeCallback,
  disableForm = false,
  name,
  companyName,
  disableNameField,
  disableCompanyNameField,
  street1,
  street2,
  city,
  region,
  postalCode,
  shippingAsBillingAddress,
  disablePhoneNumberField = false,
  phoneNumber,
  extension,
  customNameErrorMessage,
  customStreet1ErrorMessage,
  customCityErrorMessage,
  customRegionErrorMessage,
  customPostalCodeErrorMessage,
  customPhoneNumberErrorMessage,
}: AddressFormFullProps) {
  const { data: states } = useGetUsStatesQuery();

  const postalCodeFormatter = (val: string) => {
    if (val.length > 5) return val.substring(0, 5) + "-" + val.substring(5, 9);
    else return val;
  };
  /**
   * Adds formatting to a phone number as the user continues to type
   * @param val
   */
  const phoneNumberFormatter = (val: string) => {
    if (val.length > 3) {
      let formattedPhone = val.substring(0, 3) + "-" + val.substring(3, 6);
      if (val.length > 6) {
        formattedPhone += "-" + val.substring(6, 10);
      }
      return formattedPhone;
    } else return val;
  };
  const handleNameChangeEvent = (event: any) => {
    name = event.target.value;
    onChangeCallback("Name", event.target.value);
  };
  const handleCompanyNameChangeEvent = (event: any) => {
    onChangeCallback("CompanyName", event.target.value);
  };
  const handleStreet1ChangeEvent = (event: any) => {
    onChangeCallback("Street1", event.target.value);
  };
  const handleStreet2ChangeEvent = (event: any) => {
    onChangeCallback("Street2", event.target.value);
  };
  const handleCityChangeEvent = (event: any) => {
    onChangeCallback("City", event.target.value);
  };
  const handleRegionChangeEvent = (event: any) => {
    onChangeCallback("RegionCD", event.target.value);
  };
  const handlePostalCodeChangeEvent = (event: any) => {
    onChangeCallback("PostalCD", event.target.value);
  };
  const handlePhoneNumberChangeEvent = (event: NumberFormatValues) => {
    onChangeCallback("PhoneNumber", event.formattedValue);
  };
  const handleExtensionChangeEvent = (event: NumberFormatValues) => {
    onChangeCallback("Extension", event.formattedValue);
  };

  const [isCompanyNameExpanded, setIsCompanyNameExpanded] = useState(false);
  function canShowCompanyNameField(): boolean {
    if (isCompanyNameExpanded || (companyName && companyName.length > 0)) return true;
    return false;
  }
  const handleClickShowCompanyName = () => {
    setIsCompanyNameExpanded(true);
  };

  return (
    <Component>
      <div className={styles.AddressForm}>
        {disableNameField == false && (
          <Row>
            <Col>
              <TextInputField
                id="shippingName"
                label="Full Name"
                required
                size="medium"
                className={styles.tallField}
                showLeftIcon={false}
                showRightIcon={false}
                value={name as string}
                onChange={handleNameChangeEvent}
                disabled={disableForm}
                pattern="[\s\S]*\S[\s\S]*"
                maxLength={FieldMaxLengths.NAME_LENGTH}
                autoComplete="name"
                error={!!customNameErrorMessage}
                errorMessage={
                  customNameErrorMessage ? (customNameErrorMessage as string) : "Please enter the name for the address."
                }
              />
            </Col>
          </Row>
        )}
        {!disableCompanyNameField && !canShowCompanyNameField() && (
          <Row className={styles.addCompanyRow}>
            <div className={styles.addCompanyButton}>
              <Button
                variant="tertiary"
                color="blue"
                size="small"
                text="Add Company Name"
                showLeftIcon
                leftIcon={<PlusSolidIcon width={16} height={16} aria-hidden />}
                showRightIcon={false}
                onClick={handleClickShowCompanyName}
              />
            </div>
          </Row>
        )}
        {!disableCompanyNameField && canShowCompanyNameField() && (
          <Row>
            <Col>
              <TextInputField
                id="companyName"
                label="Company Name"
                optional
                size="medium"
                className={styles.tallField}
                showLeftIcon={false}
                showRightIcon={false}
                value={companyName ?? ""}
                onChange={handleCompanyNameChangeEvent}
                disabled={disableForm}
                pattern="[\s\S]*\S[\s\S]*"
                autoComplete="organization"
                errorMessage="Please enter a company name."
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col>
            <TextInputField
              id="shippingAddr1"
              label="Street Address"
              required
              size="medium"
              className={styles.tallField}
              showLeftIcon={false}
              showRightIcon={false}
              value={street1}
              onChange={handleStreet1ChangeEvent}
              disabled={disableForm || shippingAsBillingAddress}
              pattern="[\s\S]*\S[\s\S]*"
              maxLength={FieldMaxLengths.ADDRESS_LENGTH}
              autoComplete="address-line1"
              error={!!customStreet1ErrorMessage}
              errorMessage={customStreet1ErrorMessage ? customStreet1ErrorMessage : "Enter an address."}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <TextInputField
              id="shippingAddr2"
              label="Apt, Suite, Building"
              optional
              size="medium"
              className={styles.tallField}
              showLeftIcon={false}
              showRightIcon={false}
              value={street2 ?? ""}
              onChange={handleStreet2ChangeEvent}
              disabled={disableForm || shippingAsBillingAddress}
              maxLength={FieldMaxLengths.ADDRESS_LENGTH}
              autoComplete="address-line2"
            />
          </Col>
        </Row>
        <Row className={styles.splitRow}>
          <Col>
            <TextInputField
              id="shippingCity"
              label="City"
              required
              size="medium"
              className={styles.tallField}
              showLeftIcon={false}
              showRightIcon={false}
              value={city}
              onChange={handleCityChangeEvent}
              disabled={disableForm || shippingAsBillingAddress}
              pattern="[\s\S]*\S[\s\S]*"
              maxLength={FieldMaxLengths.ADDRESS_LENGTH}
              autoComplete="address-level2"
              error={!!customCityErrorMessage}
              errorMessage={customCityErrorMessage ? customCityErrorMessage : "Enter a city."}
            />
          </Col>
          <Col>
            <Dropdown
              id="shippingState"
              label="State"
              required
              value={region}
              onChange={handleRegionChangeEvent}
              disabled={disableForm || shippingAsBillingAddress}
              error={!!customRegionErrorMessage}
              errorMessage={customRegionErrorMessage ? customRegionErrorMessage : "Select a state."}
            >
              <option value="" disabled>
                Select a State
              </option>
              {states?.map((state) => (
                <option key={state.Code} value={state.Code}>
                  {state.Name}
                </option>
              ))}
            </Dropdown>
          </Col>
        </Row>
        <Row className={styles.splitRow}>
          <Col>
            <NumberFormatBase<TextInputFieldProps>
              customInput={TextInputField}
              id="shippingZip"
              label="ZIP Code"
              required
              size="medium"
              className={styles.tallField}
              showLeftIcon={false}
              showRightIcon={false}
              inputMode="numeric"
              autoComplete="postal-code"
              disabled={disableForm || shippingAsBillingAddress}
              onChange={handlePostalCodeChangeEvent}
              value={postalCode}
              format={postalCodeFormatter}
              pattern="(\d{5}([\-]\d{4})?)"
              error={!!customPostalCodeErrorMessage}
              errorMessage={customPostalCodeErrorMessage ? customPostalCodeErrorMessage : "Enter a valid postal code."}
            />
          </Col>
          <Col aria-hidden="true" />
        </Row>
        {disablePhoneNumberField == false && (
          <Row className={styles.splitRow}>
            <Col>
              <NumberFormatBase<TextInputFieldProps>
                customInput={TextInputField}
                id="shippingPhone"
                label="Phone Number"
                required
                size="medium"
                className={styles.tallField}
                showLeftIcon={false}
                showRightIcon={false}
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                format={phoneNumberFormatter}
                disabled={disableForm}
                onValueChange={handlePhoneNumberChangeEvent}
                value={phoneNumber as string}
                pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                error={!!customPhoneNumberErrorMessage}
                errorMessage={
                  customPhoneNumberErrorMessage
                    ? (customPhoneNumberErrorMessage as string)
                    : "Please enter a valid phone number."
                }
              />
            </Col>
            <Col>
              <PatternFormat<TextInputFieldProps>
                customInput={TextInputField}
                format="####"
                id="shippingExt"
                label="Extension"
                optional
                size="medium"
                className={styles.tallField}
                showLeftIcon={false}
                showRightIcon={false}
                inputMode="numeric"
                disabled={disableForm}
                onValueChange={handleExtensionChangeEvent}
                value={extension}
              />
            </Col>
          </Row>
        )}
      </div>
    </Component>
  );
}

type AddressFormProps = {
  disableForm?: boolean;
  companyName?: string;
  street1: string;
  customStreet1ErrorMessage?: string;
  street2: string;
  city: string;
  customCityErrorMessage?: string;
  region: string;
  customRegionErrorMessage?: string;
  postalCode: string;
  shippingAsBillingAddress?: boolean;
  customPostalCodeErrorMessage?: string;
  showMaximumLengthExceededErrorMessages?: boolean;
  name?: string;
  phoneNumber?: string;
  extension?: string;
  customNameErrorMessage?: string;
  customPhoneNumberErrorMessage?: string;
  onChangeCallback: (
    key: keyof Account.Addresses.NewAddress | "Street1" | "Street2" | "Extension",
    value: string,
  ) => void;
} & (
  | { disableNameField: true }
  | { disableNameField?: false | undefined; name: string; customNameErrorMessage?: string }
) &
  (
    | { disablePhoneNumberField: true }
    | {
        disablePhoneNumberField?: false | undefined;
        phoneNumber: string;
        customPhoneNumberErrorMessage?: string;
        extension: string;
      }
  ) &
  ({ disableCompanyNameField: true } | { disableCompanyNameField?: false | undefined; companyName?: string });

type AddressFormFullProps = AddressFormProps & {
  as: React.ElementType;
};
